import React, { useState, useEffect } from "react";
import { Dialog, Button, Spinner } from "@ui";
import { useSystemPrompt, useUpdateSystemPrompt } from "@agentic_system/api/system-prompt";
import { useQuery } from "@tanstack/react-query";
import { StructuredRenderer } from "@ui/components/spec-renderer";
import axios from "axios";
import { Save } from "lucide-react";

interface SchemaBuilderDocsDialogProps {
  open: boolean;
  onClose: () => void;
}

interface CatalogComponent {
  name: string;
  description: string;
  props: Record<string, string>;
  accepts_children: boolean;
  example: { spec: Record<string, unknown>; data: Record<string, unknown> } | null;
}

function useCatalogComponents() {
  return useQuery({
    queryKey: ["render_catalog", "components"],
    queryFn: async (): Promise<CatalogComponent[]> => {
      const { data } = await axios.get("/system/agentic/render_catalog/components.json");
      return data;
    },
  });
}

export function SchemaBuilderDocsDialog({ open, onClose }: SchemaBuilderDocsDialogProps) {
  const { data: prompt, isLoading: promptLoading } = useSystemPrompt("schema_builder");
  const { data: components, isLoading: catalogLoading } = useCatalogComponents();
  const updatePrompt = useUpdateSystemPrompt("schema_builder");
  const [content, setContent] = useState("");
  const [dirty, setDirty] = useState(false);
  const [previewComponent, setPreviewComponent] = useState<CatalogComponent | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (prompt?.content) {
      setContent(prompt.content);
      setDirty(false);
    }
  }, [prompt?.content]);

  const handleSave = () => {
    if (!content.trim()) return;
    updatePrompt.mutate(content.trim(), {
      onSuccess: () => setDirty(false),
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title="Documentation"
        subtitle="Composants et instructions pour le Schema Builder"
        className="max-w-5xl !p-0 [&>*]:!m-0 [&>div:first-child]:px-6 [&>div:first-child]:py-3"
      >
        <div className="border-b border-slate-200" />
        <div className="flex gap-6 max-h-[calc(100vh-12rem)] px-6 py-4">
          {/* Left — Component catalog */}
          <div className="flex-1 basis-0 min-w-0 flex flex-col">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex-shrink-0">Composants disponibles</h3>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher..."
              className="w-full mb-3 flex-shrink-0 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-default-500 focus:border-default-500"
            />
            {catalogLoading ? (
              <div className="flex items-center justify-center py-8"><Spinner /></div>
            ) : (
              <div className="overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100 min-h-0">
                {(components ?? []).filter((c) => !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.description.toLowerCase().includes(search.toLowerCase())).map((c) => (
                  <button
                    key={c.name}
                    type="button"
                    onClick={() => setPreviewComponent(c)}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50/70 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800">{c.name}</span>
                      {c.accepts_children && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">children</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">{c.description}</p>
                    {Object.keys(c.props).length > 0 && (
                      <p className="text-xs text-slate-400 mt-0.5">
                        Props: <span className="font-mono">{Object.keys(c.props).join(", ")}</span>
                      </p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right — User system prompt */}
          <div className="flex-1 basis-0 min-w-0 flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700">Instructions utilisateur</h3>
              {dirty && (
                <Button
                  size="sm"
                  icon={Save}
                  onClick={handleSave}
                  loading={updatePrompt.isPending}
                >
                  Enregistrer
                </Button>
              )}
            </div>

            {promptLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <Spinner />
              </div>
            ) : (
              <>
                <textarea
                  value={content}
                  onChange={(e) => { setContent(e.target.value); setDirty(true); }}
                  rows={20}
                  className="flex-1 px-4 py-3 border border-slate-300 rounded-lg text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-default-500 focus:border-default-500"
                  placeholder={`Ajoutez des instructions specifiques pour guider le Schema Builder...\n\nExemples :\n- Toujours utiliser Card pour grouper chaque resultat\n- Preferer CheckList rouge pour les risques\n- Limiter les ScoreBar a un max de 20\n- Utiliser Grid cols=3 pour les metriques cles`}
                />
                <p className="text-xs text-slate-400 mt-2">
                  Ces instructions sont injectees dans le prompt systeme du Schema Builder et seront appliquees dans tous les agents qui utilisent des schemas de rendu.
                  {prompt?.created_at && (
                    <span>
                      {" "}Derniere modification : {new Date(prompt.created_at).toLocaleDateString("fr-FR", {
                        day: "numeric", month: "short", year: "numeric",
                        hour: "2-digit", minute: "2-digit",
                      })}
                    </span>
                  )}
                </p>
              </>
            )}
          </div>
        </div>
      </Dialog>

      {/* Component preview dialog */}
      {previewComponent && (
        <Dialog
          open={!!previewComponent}
          onClose={() => setPreviewComponent(null)}
          title={previewComponent.name}
          subtitle={previewComponent.description}
          className="max-w-lg !p-0 [&>*]:!m-0 [&>div:first-child]:px-6 [&>div:first-child]:py-3"
        >
          <div className="border-b border-slate-200" />
          <div className="px-6 py-4">
            {/* Props */}
            {Object.keys(previewComponent.props).length > 0 && (
              <div className="mb-4">
                <h4 className="text-xs font-semibold text-slate-500 mb-2">Props</h4>
                <div className="space-y-1">
                  {Object.entries(previewComponent.props).map(([name, type]) => (
                    <div key={name} className="flex items-baseline gap-2 text-sm">
                      <span className="font-mono text-slate-700">{name}</span>
                      <span className="text-xs text-slate-400">{type}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Live preview */}
            {previewComponent.example && (
              <>
                <h4 className="text-xs font-semibold text-slate-500 mb-2">Preview</h4>
                <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/70">
                  <StructuredRenderer
                    spec={previewComponent.example.spec as any}
                    data={previewComponent.example.data}
                  />
                </div>
              </>
            )}
          </div>
        </Dialog>
      )}
    </>
  );
}
